import fetchWithAuth from '../components/fetchWithAuth'; // ✅ SECURITY: Use CSRF-protected fetch
import config from '../config';
import { cachedRequest } from '../utils/requestCache';



const API_URL = `${config.API_BASE_URL}/api/employee`;

const employeeService = {
    // Obtener todos los empleados. signal permite cancelar la request
    // Comparte la petición en vuelo y cachea solo respuestas exitosas (TASK-122)
    getAll: (branchId = null, signal) => {
        const url = branchId ? `${API_URL}?branchId=${branchId}` : API_URL;
        return cachedRequest(url, async () => {
            const response = await fetchWithAuth(url);
            if (!response.ok) {
                // Se lanza en lugar de devolver []: una lista vacía por un 429 o un 500 se confundía con
                // "no hay personal" y la vista invitaba a cargar el primer integrante (TASK-123).
                // Cada llamador decide cómo mostrar el fallo. Al lanzar, el fallo tampoco queda cacheado.
                const error = new Error(`Error ${response.status} al obtener empleados`);
                error.status = response.status;
                throw error;
            }
            const data = await response.json().catch(() => ([]));
            return Array.isArray(data) ? data : [];
        }, { signal });
    },

    // Obtener empleado por ID con sus horarios
    getById: async (id, signal) => {
        const response = await fetchWithAuth(`${API_URL}/${id}`, { signal });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Error al obtener empleado');
        }
        return response.json();
    },

    // Obtener perfil del profesional autenticado
    getMyProfile: async (signal) => {
        const response = await fetchWithAuth(`${API_URL}/profile/me`, { signal });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Error al obtener perfil');
        }
        return response.json();
    },

    // Actualizar perfil del profesional autenticado
    updateMyProfile: async (profileData) => {
        const isFormData = profileData instanceof FormData;
        // TASK-147: fetchWithAuth manda las cookies de sesión y el token CSRF. Antes se usaba
        // fetch con un token de localStorage que ya no existe (las sesiones son cookies
        // HttpOnly): el profesional nunca podía guardar su perfil (401/403).
        const response = await fetchWithAuth(`${API_URL}/profile/me`, {
            method: 'PUT',
            body: isFormData ? profileData : JSON.stringify(profileData),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Error al actualizar perfil');
        }
        return response.json();
    },

    create: async (employeeData) => {
        const isFormData = employeeData instanceof FormData;
        const response = await fetchWithAuth(API_URL, {
            method: 'POST',
            body: isFormData ? employeeData : JSON.stringify(employeeData),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                error: true,
                message: errorData.message || errorData.error || 'Error al crear empleado',
                code: errorData.error || errorData.code,
                resource: errorData.resource,
                details: errorData.details || {},
            };
        }
        return response.json().catch(() => ({}));
    },

    update: async (id, employeeData) => {
        const isFormData = employeeData instanceof FormData;
        const response = await fetchWithAuth(`${API_URL}/${id}`, {
            method: 'PUT',
            body: isFormData ? employeeData : JSON.stringify(employeeData),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { error: true, message: errorData.message || errorData.error || 'Error al actualizar empleado' };
        }
        return response.json().catch(() => ({}));
    },


    delete: async (id) => {
        // Enviar request DELETE al backend para desactivar el empleado
        const response = await fetchWithAuth(`${API_URL}/${id}`, {
            method: 'DELETE',
        });
        // Si la respuesta no es exitosa, parsear el error del backend
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { error: true, ...errorData };
        }
        // Respuesta exitosa: retornar objeto vacío sin error
        return {};
    },


    // ✅ MÓDULO 2: Obtener reporte de liquidaciones de comisiones por rango de fecha
    getCommissionsReport: async (params = {}) => {
        const queryParams = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_URL}/settlements?${queryParams}`);
        return response.json();
    }
};


export default employeeService;

